import Vue from 'vue'
import axios from 'axios'

const HTTP_STATUS_UNAUTHORIZED = 401
const HTTP_PAYMENT_REQUIRED = 402
const HTTP_STATUS_FORBIDDEN = 403
const HTTP_STATUS_AUTHENTICATION_EXPIRED = 419
const HTTP_STATUS_SERVICE_UNAVAILABLE = 503
const HTTP_STATUS_UNPROCESSABLE = 422

// Statuses the API returns to say "you may not do this", rather than to signal
// that something went wrong in the browser. They are handled by the caller (or
// by the session redirect in AppLayout) and are not worth reporting.
const EXPECTED_STATUSES = [
  HTTP_STATUS_UNAUTHORIZED,
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_AUTHENTICATION_EXPIRED,
]

const http = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
})

http.defaults.withCredentials = true

http.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

const isAxiosError = error => Boolean(error && error.isAxiosError)

// The browser cancelled the request before it completed, which is what happens
// when the user navigates away or closes the tab with a request in flight.
const isRequestAborted = error =>
  isAxiosError(error) && error.message === 'Request aborted'

const isRequestTimeout = error =>
  isAxiosError(error) &&
  !error.response &&
  !isRequestAborted(error) &&
  ['ECONNABORTED', 'ETIMEDOUT'].includes(error.code)

// Axios reports a failed connection with neither a response nor a code.
const isNetworkError = error =>
  isAxiosError(error) && !error.response && !error.code

// Request failures caused by the network or by the API declining the request,
// as opposed to a bug in our code. SentryLogger keeps these out of Sentry.
const isExpectedRequestFailure = error => {
  if (!isAxiosError(error)) {
    return false
  }

  if (
    isRequestAborted(error) ||
    isRequestTimeout(error) ||
    isNetworkError(error)
  ) {
    return true
  }

  return EXPECTED_STATUSES.includes(error.response?.status)
}

const onError = error => {
  if (error.response?.status === HTTP_STATUS_SERVICE_UNAVAILABLE) {
    window.location = '/maintenance.html'

    return
  }

  return Promise.reject(error)
}

const onSuccess = response => response

http.interceptors.response.use(onSuccess, onError)

Vue.prototype.$http = http

export default http
export {
  HTTP_STATUS_UNAUTHORIZED,
  HTTP_STATUS_AUTHENTICATION_EXPIRED,
  HTTP_STATUS_SERVICE_UNAVAILABLE,
  HTTP_PAYMENT_REQUIRED,
  HTTP_STATUS_UNPROCESSABLE,
  HTTP_STATUS_FORBIDDEN,
  isAxiosError,
  isNetworkError,
  isRequestAborted,
  isRequestTimeout,
  isExpectedRequestFailure,
}
